/**
 * Alt Text Validation Utilities
 * 
 * Comprehensive alt text validation following WCAG 2.1 guidelines
 * Platform-specific requirements and accessibility best practices
 * 
 * @author AI Assistant
 * @created 2024
 */

// Platform-specific alt text limits
export const ALT_TEXT_LIMITS = {
  twitter: 1000,
  facebook: 255,
  instagram: 255,
  linkedin: 120,
  tiktok: 300,
  threads: 255,
  bluesky: 300,
  mastodon: 500,
  default: 125
};

// Words that should be avoided in alt text (redundant phrases)
const REDUNDANT_PHRASES = [
  'image of',
  'picture of',
  'photo of',
  'graphic of',
  'illustration of',
  'screenshot of',
  'image showing',
  'picture showing',
  'photo showing',
  'image that shows',
  'picture that shows',
  'photo that shows',
  'this is an image of',
  'this is a picture of',
  'this is a photo of'
];

// Words that indicate good descriptive alt text
const DESCRIPTIVE_INDICATORS = [
  'wearing', 'holding', 'sitting', 'standing', 'walking', 'running',
  'smiling', 'laughing', 'looking', 'pointing', 'gesturing',
  'red', 'blue', 'green', 'yellow', 'black', 'white', 'colorful',
  'bright', 'dark', 'light', 'large', 'small', 'tall', 'short',
  'outdoor', 'indoor', 'sunny', 'cloudy', 'night', 'day',
  'professional', 'casual', 'business', 'event', 'meeting'
];

/**
 * Get maximum alt text length for platforms
 * @param {Array<string>} platforms - Array of platform names
 * @returns {number} Maximum allowed alt text length
 */
export const getMaxAltTextLength = (platforms = []) => {
  if (!platforms || platforms.length === 0) {
    return ALT_TEXT_LIMITS.default;
  }
  
  // Find the most restrictive limit among selected platforms
  const platformLimits = platforms.map(platform => {
    const normalizedPlatform = platform.toLowerCase();
    return ALT_TEXT_LIMITS[normalizedPlatform] || ALT_TEXT_LIMITS.default;
  });
  
  return Math.min(...platformLimits);
};

/**
 * Check if alt text contains redundant phrases
 * @param {string} altText - Alt text to check
 * @returns {Array<string>} Array of redundant phrases found
 */
export const findRedundantPhrases = (altText) => {
  if (!altText) return [];
  
  const lowerAltText = altText.toLowerCase();
  return REDUNDANT_PHRASES.filter(phrase => 
    lowerAltText.includes(phrase.toLowerCase())
  );
};

/**
 * Analyze alt text quality based on WCAG guidelines
 * @param {string} altText - Alt text to analyze
 * @returns {Object} Quality analysis result
 */
export const analyzeAltTextQuality = (altText) => {
  const analysis = {
    hasContent: false,
    hasDescriptiveWords: false,
    hasRedundantPhrases: false,
    redundantPhrases: [],
    descriptiveWordCount: 0,
    wordCount: 0,
    score: 0, // 0-100
    suggestions: []
  };
  
  if (!altText || altText.trim() === '') {
    analysis.suggestions.push('Add descriptive alt text for accessibility');
    return analysis;
  }
  
  const trimmedAltText = altText.trim();
  analysis.hasContent = trimmedAltText.length > 0;
  
  // Word count analysis
  const words = trimmedAltText.split(/\s+/).filter(word => word.length > 0);
  analysis.wordCount = words.length;
  
  // Check for redundant phrases
  analysis.redundantPhrases = findRedundantPhrases(trimmedAltText);
  analysis.hasRedundantPhrases = analysis.redundantPhrases.length > 0;
  
  // Check for descriptive words
  const lowerAltText = trimmedAltText.toLowerCase();
  analysis.descriptiveWordCount = DESCRIPTIVE_INDICATORS.filter(indicator =>
    lowerAltText.includes(indicator)
  ).length;
  analysis.hasDescriptiveWords = analysis.descriptiveWordCount > 0;
  
  // Calculate quality score
  let score = 0;
  
  // Base points for having content
  if (analysis.hasContent) score += 20;
  
  // Points for word count (optimal 5-15 words)
  if (analysis.wordCount >= 3 && analysis.wordCount <= 20) {
    score += 30;
  } else if (analysis.wordCount > 20) {
    score += 15; // Penalty for too long
  } else if (analysis.wordCount > 0) {
    score += 10; // Some points for having words
  }
  
  // Points for descriptive content
  if (analysis.hasDescriptiveWords) {
    score += Math.min(analysis.descriptiveWordCount * 10, 30);
  }
  
  // Penalty for redundant phrases
  if (analysis.hasRedundantPhrases) {
    score -= analysis.redundantPhrases.length * 10;
  }
  
  // Ensure score is within bounds
  analysis.score = Math.max(0, Math.min(100, score));
  
  // Generate suggestions
  if (analysis.wordCount < 3) {
    analysis.suggestions.push('Add more descriptive details (aim for 5-15 words)');
  }
  
  if (analysis.wordCount > 20) {
    analysis.suggestions.push('Consider shortening for better screen reader experience');
  }
  
  if (analysis.hasRedundantPhrases) {
    analysis.suggestions.push(`Remove redundant phrases: "${analysis.redundantPhrases.join('", "')}"`);
  }
  
  if (!analysis.hasDescriptiveWords && analysis.wordCount > 0) {
    analysis.suggestions.push('Add more specific visual details (colors, actions, emotions)');
  }
  
  if (analysis.score < 40) {
    analysis.suggestions.push('Consider describing what\'s important about the image in context');
  }
  
  return analysis;
};

/**
 * Validate alt text against requirements
 * @param {string} altText - Alt text to validate
 * @param {Array<string>} platforms - Target platforms
 * @param {Object} options - Validation options
 * @returns {Object} Validation result
 */
export const validateAltText = (altText, platforms = [], options = {}) => {
  const {
    requireAltText = false,
    enforceQuality = false,
    minQualityScore = 40
  } = options;
  
  const result = {
    isValid: true,
    isRequired: requireAltText,
    errors: [],
    warnings: [],
    suggestions: [],
    length: altText ? altText.trim().length : 0,
    maxLength: getMaxAltTextLength(platforms),
    quality: null,
    compliance: {
      wcag: true,
      platforms: true
    }
  };
  
  // Check if alt text is required but missing
  if (requireAltText && (!altText || altText.trim() === '')) {
    result.isValid = false;
    result.compliance.wcag = false;
    result.errors.push('Alt text is required for accessibility compliance');
    return result;
  }
  
  // If no alt text provided and not required, return early
  if (!altText || altText.trim() === '') {
    if (!requireAltText) {
      result.warnings.push('Consider adding alt text for better accessibility');
    }
    return result;
  }
  
  const trimmedAltText = altText.trim();
  
  // Check length limits
  if (trimmedAltText.length > result.maxLength) {
    result.isValid = false;
    result.compliance.platforms = false;
    const restrictivePlatform = platforms.find(p => 
      ALT_TEXT_LIMITS[p.toLowerCase()] === result.maxLength
    );
    result.errors.push(
      `Alt text exceeds ${result.maxLength} character limit` + 
      (restrictivePlatform ? ` for ${restrictivePlatform}` : '')
    );
  }
  
  // Analyze quality
  result.quality = analyzeAltTextQuality(trimmedAltText);
  
  // Quality enforcement
  if (enforceQuality && result.quality.score < minQualityScore) {
    result.warnings.push(`Alt text quality could be improved (score: ${result.quality.score}/100)`);
    result.suggestions.push(...result.quality.suggestions);
  } else {
    result.suggestions.push(...result.quality.suggestions);
  }
  
  // Add warnings for redundant phrases
  if (result.quality.hasRedundantPhrases) {
    result.warnings.push('Alt text contains redundant phrases that can be removed');
  }
  
  // Add suggestions for better accessibility
  if (result.quality.wordCount > 0 && result.quality.score >= minQualityScore) {
    // Good alt text, no additional suggestions needed
  } else if (result.quality.wordCount === 0) {
    result.suggestions.push('Describe the key visual elements and their context');
  }
  
  return result;
};

/**
 * Get alt text validation summary for UI display
 * @param {Object} validation - Validation result from validateAltText
 * @returns {Object} UI-friendly summary
 */
export const getAltTextValidationSummary = (validation) => {
  let status = 'success';
  let color = 'success';
  let primaryMessage = '';
  
  if (!validation.isValid) {
    status = 'error';
    color = 'error';
    primaryMessage = validation.errors[0];
  } else if (validation.warnings.length > 0) {
    status = 'warning';
    color = 'warning';
    primaryMessage = validation.warnings[0];
  } else if (validation.length === 0) {
    status = 'info';
    color = 'info';
    primaryMessage = validation.isRequired ? 'Alt text required' : 'Alt text recommended';
  } else {
    status = 'success';
    color = 'success';
    primaryMessage = validation.quality 
      ? `Alt text quality: ${validation.quality.score}/100`
      : 'Alt text provided';
  }
  
  return {
    status,
    color,
    primaryMessage,
    hasErrors: validation.errors.length > 0,
    hasWarnings: validation.warnings.length > 0,
    hasSuggestions: validation.suggestions.length > 0,
    allMessages: [
      ...validation.errors,
      ...validation.warnings,
      ...validation.suggestions
    ],
    quality: validation.quality,
    compliance: validation.compliance
  };
};

/**
 * Generate alt text guidance based on context
 * @param {string} context - Context of the image (post, profile, etc.)
 * @returns {Object} Guidance object with tips and examples
 */
export const getAltTextGuidance = (context = 'post') => {
  const commonTips = [
    'Describe the essential visual information',
    'Keep it concise but informative',
    'Include important text visible in the image',
    'Don\'t start with "Image of" or "Picture of"',
    'Focus on what\'s relevant to your message'
  ];
  
  const contextSpecific = {
    post: {
      tips: [
        'Describe what supports your post\'s message',
        'Include relevant details about people, objects, or text',
        'Consider the emotional tone if relevant'
      ],
      examples: [
        'Woman in blue dress presenting to a group of colleagues',
        'Graph showing 25% increase in sales over three months',
        'Sunset over mountain range with orange and pink clouds'
      ]
    },
    profile: {
      tips: [
        'Describe your appearance and setting',
        'Include professional context if relevant',
        'Keep it brief but descriptive'
      ],
      examples: [
        'Professional headshot of person in navy suit',
        'Person working at laptop in modern office',
        'Casual photo of person smiling outdoors'
      ]
    },
    product: {
      tips: [
        'Describe the product clearly',
        'Include key features or benefits visible',
        'Mention colors, size, or other important attributes'
      ],
      examples: [
        'Red wireless headphones on white wooden desk',
        'Stack of three books with colorful covers',
        'Smartphone displaying app interface'
      ]
    }
  };
  
  return {
    tips: [
      ...commonTips,
      ...(contextSpecific[context]?.tips || [])
    ],
    examples: contextSpecific[context]?.examples || [],
    resources: [
      'WCAG 2.1 Guidelines: https://www.w3.org/WAI/WCAG21/Understanding/images-of-text.html',
      'Alt Text Best Practices: https://webaim.org/techniques/alttext/',
      'Screen Reader Testing: https://webaim.org/articles/screenreader_testing/'
    ]
  };
};

/**
 * Check if image requires alt text based on content analysis
 * @param {string} imageUrl - URL of the image
 * @param {string} postContext - Context of the post
 * @returns {Promise<Object>} Analysis result
 */
export const analyzeImageForAltTextRequirement = async (imageUrl, postContext = '') => {
  // This is a placeholder for future AI/ML integration
  // For now, we'll return a simple heuristic-based analysis
  
  return new Promise((resolve) => {
    // Simple heuristic: if there's text in the post context, 
    // the image likely adds visual information that needs description
    const hasPostContext = postContext && postContext.trim().length > 0;
    
    resolve({
      requiresAltText: true, // Default to requiring alt text
      confidence: hasPostContext ? 0.8 : 0.6,
      reason: hasPostContext 
        ? 'Image likely provides additional context to the post'
        : 'Image content should be described for accessibility',
      suggestions: [
        'Describe key visual elements',
        'Include any text visible in the image',
        'Consider the image\'s purpose in your post'
      ]
    });
  });
};

export default {
  ALT_TEXT_LIMITS,
  getMaxAltTextLength,
  findRedundantPhrases,
  analyzeAltTextQuality,
  validateAltText,
  getAltTextValidationSummary,
  getAltTextGuidance,
  analyzeImageForAltTextRequirement
}; 